// @flow
import { AsyncRequest, createReducer } from 'angieslist-app-platform';
import serviceProviderActionKeys from './serviceProviderActionKeys';
import serviceProviderActions from './serviceProviderActions';
import type {
  GetAdsServiceProvidersInfoResponse,
  GetCurrentUsersServiceProvidersRequest,
  GetCurrentUsersServiceProvidersResponse,
  GetServiceProviderRequest,
  GetServiceProvidersRequest,
  GetServiceProviderResponse,
  GetServiceProvidersResponse,
  GetServiceProviderMetricsResponse,
  GetServiceProviderAccountOwnerResponse,
} from './serviceProviderService';
import type {
  Action,
  AsyncState,
  ServiceProvider,
} from '../../types';

type GetCurrentUsersServiceProviderSuccessAction = {
  payload: {
    body: GetCurrentUsersServiceProvidersResponse,
  }
};

type GetServiceProviderSuccessAction = Action<{
  body: GetServiceProviderResponse,
}>;

type GetAdsServiceProviderInfoSuccessAction = Action<{
  body: GetAdsServiceProvidersInfoResponse,
}>;

type GetServiceProviderMetricsSuccessAction = {
  payload: {
    body: GetServiceProviderMetricsResponse,
  }
};

type GetServiceProviderAccountOwnerSuccessAction = {
  payload: {
    body: GetServiceProviderAccountOwnerResponse
  }
}

const reduceToObjectByIdAndShallowMerge = (accumulator, currentValue) => {
  const oldValue = accumulator[String(currentValue.id)] || {};
  return {
    ...accumulator,
    [currentValue.id]: {
      // maintain old property values even if they might be stale
      ...oldValue,
      ...currentValue,
    },
  };
};

export type GetCurrentUsersServiceProvidersState =
AsyncState<GetCurrentUsersServiceProvidersRequest, GetCurrentUsersServiceProvidersResponse>;

export const getCurrentUsersServiceProviders: any =
AsyncRequest.createReducer(serviceProviderActionKeys.GET_CURRENT_USERS_SERVICE_PROVIDERS);

export type GetServiceProviderState =
AsyncState<GetServiceProviderRequest, GetServiceProviderResponse>;

export type GetServiceProvidersState =
AsyncState<GetServiceProvidersRequest, GetServiceProvidersResponse>;

export const getServiceProvider: any =
AsyncRequest.createReducer(serviceProviderActionKeys.GET_SERVICE_PROVIDER);

export const leadServiceProvider: any =
AsyncRequest.createReducer(serviceProviderActionKeys.GET_LEAD_SERVICE_PROVIDER);

export const spProductPackage: any =
AsyncRequest.createReducer(serviceProviderActionKeys.GET_SP_PRODUCT_PACKAGE);

export const proProductPackage: any =
  AsyncRequest.createReducer(serviceProviderActionKeys.GET_PRO_PRODUCT_PACKAGE);

// Maintain serviceProviderId
export type ServiceProviderIdState = ?string;
export const serviceProviderId: any = createReducer(null, {
  [serviceProviderActionKeys.UPDATE_SERVICE_PROVIDER_ID]:
    (
      state,
      { payload },
    ) => payload,
}, false);

// Maintain object map of all service providers.
export type ServiceProviderCacheState = {
  [serviceProviderId: string]: ServiceProvider,
};

export const serviceProviderCache: any = createReducer({}, {
  [serviceProviderActionKeys.ADD_ANGI_LEADS_PRO_TO_SERVICE_PROVIDER_CACHE]:
    (
      state: ServiceProviderCacheState,
      action,
    ): ServiceProviderCacheState => ({
      ...state,
      [action.payload.angiLeadsSpid]: {
        ...state[action.payload.angiLeadsSpid],
        isAngiLeads: true,
      },
    }),

  [serviceProviderActionKeys.GET_CURRENT_USERS_SERVICE_PROVIDERS_SUCCESS]:
    (
      state: ServiceProviderCacheState,
      action: GetCurrentUsersServiceProviderSuccessAction,
    ): ServiceProviderCacheState => ({
      ...(action.payload.body.serviceProviders.reduce(reduceToObjectByIdAndShallowMerge, state)),
    }),

  [serviceProviderActionKeys.GET_SERVICE_PROVIDER_START]: (state, action) => {
    action.addSideEffect((store) => {
      const getUnreadCount = () =>
        store.dispatch(serviceProviderActions
          .getServiceProviderUnreadCounts(action.payload.serviceProviderId));
      getUnreadCount();
    });
    return state;
  },

  [serviceProviderActionKeys.GET_SERVICE_PROVIDER_UNREAD_COUNTS_SUCCESS]:
    (
      state: ServiceProviderCacheState,
      action: any,
    ): ServiceProviderCacheState => ({
      ...(action.payload.body.reduce(reduceToObjectByIdAndShallowMerge, state)),
    }),

  [serviceProviderActionKeys.GET_ALL_SERVICE_PROVIDER_UNREAD_COUNTS_SUCCESS]:
    (
      state: ServiceProviderCacheState,
      action: any,
    ): ServiceProviderCacheState => ({
      ...(action.payload.body.reduce(reduceToObjectByIdAndShallowMerge, state)),
    }),

  [serviceProviderActionKeys.GET_SERVICE_PROVIDER_SUCCESS]:
    (
      state: ServiceProviderCacheState,
      action: GetServiceProviderSuccessAction,
    ): ServiceProviderCacheState => {
      const { serviceProviders } = action.payload.body;
      action.addSideEffect((store) => {
        store.dispatch(serviceProviderActions.getLeafSlug(serviceProviders));
      });
      return (serviceProviders.reduce(reduceToObjectByIdAndShallowMerge, state));
    },

  [serviceProviderActionKeys.GET_ADS_SERVICE_PROVIDER_SUCCESS]:
    (
      state: ServiceProviderCacheState,
      action: GetServiceProviderSuccessAction,
    ): ServiceProviderCacheState => {
      const { serviceProviders } = action.payload.body;
      action.addSideEffect((store) => {
        store.dispatch(serviceProviderActions.getLeafSlug(serviceProviders));
      });
      return (serviceProviders.reduce(reduceToObjectByIdAndShallowMerge, state));
    },

  [serviceProviderActionKeys.GET_ADS_SERVICE_PROVIDER_INFO_SUCCESS]:
    (
      state: ServiceProviderCacheState,
      action: GetAdsServiceProviderInfoSuccessAction,
    ): ServiceProviderCacheState => ({
      ...state,
      [action.payload.body.serviceProviderId]: {
        ...state[String(action.payload.body.serviceProviderId)],
        isAgreementActiveAndOnlineEnroll: action.payload.body.isAgreementActiveAndOnlineEnroll,
      },
    }),

  [serviceProviderActionKeys.GET_SERVICE_PROVIDER_METRICS_SUCCESS]:
    (
      state: ServiceProviderCacheState,
      action: GetServiceProviderMetricsSuccessAction,
    ): ServiceProviderCacheState => ({
      ...state,
      [action.payload.body.serviceProviderId]: {
        ...state[String(action.payload.body.serviceProviderId)],
        metrics: action.payload.body,
      },
    }),

  [serviceProviderActionKeys.GET_SERVICE_PROVIDER_ACCOUNT_OWNER_SUCCESS]:
    (
      state: ServiceProviderCacheState,
      action: GetServiceProviderAccountOwnerSuccessAction,
    ): ServiceProviderCacheState => ({
      ...state,
      [action.payload.body.serviceProviderId]: {
        ...state[String(action.payload.body.serviceProviderId)],
        accountOwner: action.payload.body,
      },
    }),
  [serviceProviderActionKeys.GET_LEAF_SLUG_SUCCESS]:
    (
      state: ServiceProviderCacheState,
      action: any,
    ): ServiceProviderCacheState => ({
      ...(action.payload.body.reduce(reduceToObjectByIdAndShallowMerge, state)),
    }),
}, false);



// WEBPACK FOOTER //
// ./src/app/common/serviceProvider/serviceProviderReducer.js